// Friends model & local store (ARCHITECTURE.md §6).
//
// A friend is identified by their Circle ID. We keep when we added them and
// whether we've verified their fingerprint out of band. Their public keys are
// derived from the Circle ID on demand, and the name shown for them is the one
// they shared in their profile — there is no local nickname, so every user sees
// the same, owner-controlled identity. Mutual connection and update delivery
// come with the relay layer; this module is the local address book.
//
// Multi-device (ARCHITECTURE.md §8.1): every entry carries the moment it last
// changed, and a removal is recorded as a tombstone rather than erased. That is
// what lets two of the user's own devices reconcile: without a timestamp there
// is no way to tell a stale copy from a fresh edit, and without a tombstone a
// deletion cannot travel — the other device would keep the contact and hand it
// back on its next backup.

import { decodeCircleId } from './keys';
import { currentIdentity } from './identity';
import { idbDelete, idbGet, idbGetAll, idbPut, STORE_FRIENDS } from './db';

export interface Friend {
  circleId: string;
  addedAt: number;
  verified: boolean;
  /** IDs of the custom groups this contact belongs to (0..n). See core/groups. */
  groups?: string[];
  /**
   * A local, owner-only placeholder name (e.g. "Mamie") set when adding this
   * contact, before their real profile has arrived. It exists purely to tell
   * pending contacts apart in the list — it is never shared with anyone, and
   * once a shared name arrives that always takes priority over it.
   */
  tempName?: string;
  /** Epoch ms of the last local change — the clock reconciliation compares. */
  updatedAt?: number;
  /** Epoch ms of removal. Set = tombstone: hidden locally, still carried. */
  deletedAt?: number;
}

export class FriendError extends Error {}

/**
 * When this entry last changed, for reconciliation. Records written before
 * timestamps existed fall back to when they were added, so they lose to any
 * explicit edit rather than winning by accident.
 */
export function friendStamp(f: Friend): number {
  return Math.max(f.deletedAt ?? 0, f.updatedAt ?? 0, f.addedAt ?? 0);
}

export function isDeleted(f: Friend): boolean {
  return f.deletedAt !== undefined;
}

/** Validate and add a friend by Circle ID, with an optional local placeholder name. */
export async function addFriend(
  circleId: string,
  tempName?: string,
): Promise<Friend & { /** True when this re-adds someone previously removed (a tombstone). */ revived: boolean }> {
  const id = circleId.trim();

  try {
    decodeCircleId(id);
  } catch {
    throw new FriendError('ID Cercle invalide');
  }

  const me = currentIdentity();
  if (me && id === me.circleId) throw new FriendError('Vous ne pouvez pas vous ajouter vous-même');

  const existing = await idbGet<Friend>(STORE_FRIENDS, id);
  // A tombstoned entry reads as absent: re-adding someone you removed is
  // allowed, and the fresh timestamp is what makes the re-add beat the removal
  // when the two devices meet again.
  if (existing && !isDeleted(existing)) {
    throw new FriendError('Cette personne est déjà dans votre Cercle');
  }

  const now = Date.now();
  const name = tempName?.trim();
  const friend: Friend = {
    circleId: id,
    addedAt: now,
    verified: false,
    groups: [],
    updatedAt: now,
    ...(name ? { tempName: name } : {}),
  };
  await idbPut(STORE_FRIENDS, id, friend);
  return { ...friend, revived: !!existing };
}

/** The visible address book — tombstones excluded. */
export async function listFriends(): Promise<Friend[]> {
  const friends = await idbGetAll<Friend>(STORE_FRIENDS);
  // Stable order by when they were added; the list UI sorts by the shared name.
  return friends.filter((f) => !isDeleted(f)).sort((a, b) => a.addedAt - b.addedAt);
}

/** Every entry including tombstones — for the backup and for reconciliation. */
export function listFriendEntries(): Promise<Friend[]> {
  return idbGetAll<Friend>(STORE_FRIENDS);
}

export async function getFriend(circleId: string): Promise<Friend | undefined> {
  const f = await idbGet<Friend>(STORE_FRIENDS, circleId);
  return f && !isDeleted(f) ? f : undefined;
}

async function update(circleId: string, patch: Partial<Friend>): Promise<Friend> {
  const existing = await idbGet<Friend>(STORE_FRIENDS, circleId);
  if (!existing || isDeleted(existing)) throw new FriendError('Ami introuvable');
  const next = { ...existing, ...patch, updatedAt: Date.now() };
  await idbPut(STORE_FRIENDS, circleId, next);
  return next;
}

export function setVerified(circleId: string, verified: boolean): Promise<Friend> {
  return update(circleId, { verified });
}

/** Set or clear the local placeholder name (empty/blank clears it). */
export async function setTempName(circleId: string, tempName: string): Promise<Friend> {
  const existing = await idbGet<Friend>(STORE_FRIENDS, circleId);
  if (!existing || isDeleted(existing)) throw new FriendError('Ami introuvable');
  const name = tempName.trim();
  const next = { ...existing, updatedAt: Date.now() };
  if (name) next.tempName = name;
  else delete next.tempName;
  await idbPut(STORE_FRIENDS, circleId, next);
  return next;
}

/** Replace a contact's group membership (list of group IDs, 0..n). */
export function setFriendGroups(circleId: string, groups: string[]): Promise<Friend> {
  return update(circleId, { groups });
}

/**
 * Remove a contact. Recorded as a tombstone rather than erased, so the removal
 * reaches the user's other devices instead of being undone by the first one
 * that still holds the contact.
 */
export async function removeFriend(circleId: string): Promise<undefined> {
  const existing = await idbGet<Friend>(STORE_FRIENDS, circleId);
  const now = Date.now();
  const tomb: Friend = existing
    ? { ...existing, groups: [], deletedAt: now, updatedAt: now }
    : { circleId, addedAt: now, verified: false, groups: [], deletedAt: now, updatedAt: now };
  await idbPut(STORE_FRIENDS, circleId, tomb);
  return undefined;
}

/** Drop a tombstone for good, once it is too old to still be news anywhere. */
export function purgeFriend(circleId: string): Promise<undefined> {
  return idbDelete(STORE_FRIENDS, circleId);
}

/**
 * Deterministic tie-break for two entries changed at the exact same instant
 * (two devices editing within the same millisecond): whichever serializes to
 * the lexicographically larger string wins. Both sides compute this over the
 * same pair of values regardless of which one is "local" to them, so a
 * genuine tie still converges to one answer instead of each device
 * stubbornly keeping its own copy forever.
 */
function wins<T>(candidate: T, other: T): boolean {
  return JSON.stringify(candidate) > JSON.stringify(other);
}

/**
 * Reconcile contacts from a self-backup with the local ones, entry by entry:
 * the more recently changed side wins, and a tombstone is an ordinary entry in
 * that comparison — a removal beats an older edit, a later re-add beats the
 * removal. Absence carries no meaning, so a device that knows nothing about a
 * contact never deletes it. Returns how many entries changed locally.
 */
export async function reconcileFriends(incoming: Friend[]): Promise<number> {
  const local = new Map((await listFriendEntries()).map((f) => [f.circleId, f]));
  let changed = 0;
  for (const remote of incoming) {
    const mine = local.get(remote.circleId);
    if (mine) {
      const cmp = friendStamp(remote) - friendStamp(mine);
      if (cmp < 0 || (cmp === 0 && !wins(remote, mine))) continue;
    }
    await idbPut(STORE_FRIENDS, remote.circleId, remote);
    changed++;
  }
  return changed;
}
